import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { merchantAuthAPI } from '../../../services/merchantAPI';

const TermsAcceptanceModal = ({ open, consentStatus }) => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { refreshProfile } = useMerchantAuth();
  const navigate = useNavigate();

  const handleAccept = async () => {
    if (!accepted) return;
    
    setLoading(true);
    try {
      // Record terms consent
      await merchantAuthAPI.recordConsent({
        consent_type: 'terms',
        version: consentStatus.latest_terms_version,
        accepted: true
      });
      
      // Record privacy consent
      await merchantAuthAPI.recordConsent({
        consent_type: 'privacy',
        version: consentStatus.latest_privacy_version,
        accepted: true
      });
      
      // Refresh profile to update auth state
      await refreshProfile();
    } catch (error) {
      console.error('Failed to record consent:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          // Normal close logic if allowed, but here we don't allow it
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '16px',
          maxWidth: '400px'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
          Terms & Conditions
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#666', mb: 3, textAlign: 'center' }}>
          To continue using Glammify, please review and accept our updated Terms and Conditions and Privacy Policy.
        </Typography>
        
        <Box sx={{ bgcolor: '#F9F5FF', p: 2, borderRadius: '12px', mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                sx={{
                  color: '#6318C6',
                  '&.Mui-checked': {
                    color: '#A864ED',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#1A1A1A' }}>
                I Accept the{' '}
                <Link
                  component="button"
                  onClick={() => {
                    navigate('/terms-conditions');
                  }}
                  sx={{ color: '#A864ED', textDecoration: 'underline', border: 'none', background: 'none', p: 0, font: 'inherit', cursor: 'pointer' }}
                >
                  Terms and Conditions
                </Link>
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={!accepted || loading}
          onClick={handleAccept}
          sx={{
            background: 'linear-gradient(90deg, #A864ED 0%, #F4B876 100%)',
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            py: 1.2,
            boxShadow: 'none',
            '&:hover': {
              background: 'linear-gradient(90deg, #9b5ce0 0%, #e8ad6b 100%)',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              background: '#E0E0E0',
              color: '#9E9E9E'
            }
          }}
        >
          {loading ? 'Processing...' : 'Accept and Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
